/**
 * Pizza Game rounds — Tutorial (Q1) + 9 game rounds (Q2..Q10).
 *
 * Each round: pick the asked fraction of the visible ingredients from the
 * tray, drop them onto the fresh pizza, evaluate.
 *
 * Numerator/denominator/total values come from the "Orchard" content spec;
 * ingredients come from the Figma pizza-game art.
 */

/**
 * @typedef {Object} Round
 * @property {string} id           "tutorial" | "q2" .. "q10"
 * @property {string} ingredient   ingredient key (matches PNG prefix in PizzaAssets/)
 * @property {string} label        display name (used in the recipe-bar text)
 * @property {number} numerator
 * @property {number} denominator
 * @property {number} total        ingredients shown on the tray this round
 * @property {number} answer       correct number of ingredients to pick = total * numerator / denominator
 */

const round = (id, ingredient, label, num, den, total) => ({
  id,
  ingredient,
  label,
  numerator: num,
  denominator: den,
  total,
  answer: (total * num) / den,
});

// Tutorial = 1/2 of 16 mushrooms → kid picks 8. Sizing/grid matches the
// game rounds so the tutorial reads as the "first round" of the same set.
export const TUTORIAL_ROUND = round('tutorial', 'mushroom', 'Mushrooms', 1, 2, 16);

// Per-round (q2..q10) mapping.
// INGREDIENT comes from the order-text-q*.png master ("NEW Order —
// {Ingredient} Pizza"). FRACTION comes from the matching recipe-text-q*.png
// exported from Figma node 1741:506. Keep the two PNG sets and this table
// in lock-step — renaming/reordering one without the others will mismatch
// the tray ingredient and the on-screen fraction.
// Totals updated to match the Figma "ingredient spec" sheet on node
// 1741:506 (Olive 15, Basil 12 — bumped from earlier guesses). The
// 1/2-of-Onion recipe-text PNG locks q2 at 8 (Figma 9 doesn't divide).
export const GAME_ROUNDS = [
  round('q2',  'onion',      'Onions',       1, 2, 8),
  round('q3',  'tomato',     'Tomatoes',     1, 4, 16),
  round('q4',  'olive',      'Olives',       1, 3, 15),
  round('q5',  'babycorn',   'Babycorns',    2, 4, 8),
  round('q6',  'bellpepper', 'Bell Peppers', 3, 4, 12),
  round('q7',  'pepperoni',  'Pepperonis',   1, 3, 12),
  round('q8',  'zucchini',   'Zuccinis',     1, 2, 10),
  round('q9',  'onion',      'Onions',       2, 3, 9),
  round('q10', 'basil',      'Basil',        1, 4, 12),
];

export const TOTAL_GAME_ROUNDS = GAME_ROUNDS.length; // 9
export const PASS_THRESHOLD = 6; // ≥6/9 to unlock the celebration end scene

// All ingredient keys we need to load (used by LoadScene to build asset keys)
export const INGREDIENT_KEYS = [
  'mushroom',
  'onion',
  'tomato',
  'olive',
  'babycorn',
  'bellpepper',
  'pepperoni',
  'zucchini',
  'basil',
];
